import { ContourComparisonError } from '~/domain/comparison/contourComparisonMetrics';
import { RoiBinaryCrop } from '~/domain/comparison/contourMaskAlignment';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';

/**
 * Largest deviation, in voxels, tolerated when checking that a crop origin
 * lands on the integer image-voxel lattice. The mapping is exact in theory
 * (crops are taken on image-voxel boundaries); this only absorbs float round-off
 * in the world↔voxel matrices.
 */
const ORIGIN_INTEGER_TOLERANCE_VOX = 1e-3;

/**
 * Read one ROI's GPU signed-distance-field texture back to the CPU and binarise
 * it into a {@link RoiBinaryCrop} positioned on the shared image-voxel lattice.
 *
 * Thresholding lives in this engine adapter (not the domain layer) because the
 * SDF iso-value {@link ContourTexture.SDF_THRESHOLD} is an engine encoding
 * detail: the field is stored as bytes in [0, 255] with the surface at 127.5;
 * foreground is every voxel at or above it, matching the contour renderer's
 * inside test. For integer bytes `>= 127.5` is exactly `>= 128`, so there is no
 * `>`/`>=` boundary ambiguity.
 *
 * The readback uses the SDF's cached pixels, so re-reading an unedited ROI is
 * cheap; the underlying `gl.readPixels` is synchronous.
 *
 * @throws ContourComparisonError if the ROI is a point ROI, has no generated
 * SDF texture, or its crop does not sit on integer image-grid voxels (which
 * would break the lattice assumption that makes alignment lossless).
 */
export function roiToBinaryCrop(roi: RegionOfInterest): RoiBinaryCrop {
    if (roi.isPointRoi) {
        throw new ContourComparisonError(
            `ROI "${roi.name}" is a point ROI and has no contour volume.`
        );
    }
    const texture = roi.texture;
    const sdf = texture?.signedDistanceField;
    if (!texture || !sdf) {
        throw new ContourComparisonError(
            `ROI "${roi.name}" has no generated contour geometry to compare.`
        );
    }

    const textureGrid = texture.textureGrid;
    // `texture.grid` is the master image grid, identical for every ROI of the
    // image; expressing the crop origin in its voxel coordinates puts both ROIs
    // on one common lattice without any per-ROI reference-frame mismatch.
    const imageGrid = texture.grid;
    const sizeX = textureGrid.sizeX;
    const sizeY = textureGrid.sizeY;
    const sizeZ = textureGrid.sizeZ;

    const slices = sdf.pixels; // cached GPU readback: slices[z][y * sizeX + x]
    if (slices.length !== sizeZ) {
        throw new ContourComparisonError(
            `ROI "${roi.name}" SDF slice count ${slices.length} does not match ` +
                `grid depth ${sizeZ}.`
        );
    }

    // textureGrid.getPosition() is the world-space corner of the crop's voxel
    // (0,0,0); mapping it through the image grid's worldToVoxel yields that
    // corner's image-voxel index, which must be integral.
    const originVoxF = imageGrid.toVoxelSpace(textureGrid.getPosition());
    const originVox: [number, number, number] = [0, 0, 0];
    for (let i = 0; i < 3; i += 1) {
        const rounded = Math.round(originVoxF[i]);
        if (Math.abs(originVoxF[i] - rounded) > ORIGIN_INTEGER_TOLERANCE_VOX) {
            throw new ContourComparisonError(
                `ROI "${roi.name}" crop is not aligned to the image voxel grid ` +
                    `(axis ${i} origin ${originVoxF[i]}).`
            );
        }
        originVox[i] = rounded;
    }

    const res = textureGrid.getRes();
    const threshold = ContourTexture.SDF_THRESHOLD;
    const sliceVoxels = sizeX * sizeY;
    const voxels = new Uint8Array(sliceVoxels * sizeZ);
    // Per-slice layout (y * sizeX + x) with z outermost matches the crop's flat
    // index x + sizeX * (y + sizeY * z), so a straight slice-by-slice copy is
    // correct without per-voxel coordinate math.
    let dst = 0;
    for (let z = 0; z < sizeZ; z += 1) {
        const slice = slices[z];
        if (slice.length !== sliceVoxels) {
            throw new ContourComparisonError(
                `ROI "${roi.name}" SDF slice ${z} length ${slice.length} does ` +
                    `not match slice size ${sliceVoxels}.`
            );
        }
        for (let i = 0; i < sliceVoxels; i += 1, dst += 1) {
            if (slice[i] >= threshold) {
                voxels[dst] = 1;
            }
        }
    }

    return {
        sizeVox: [sizeX, sizeY, sizeZ],
        originVox,
        spacingMm: [res[0], res[1], res[2]],
        voxels,
    };
}
